import { ResponsiveTable } from '../components/common/ResponsiveTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import TreatmentUsageReport from '../components/reports/TreatmentUsageReport';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Laboratory } from '../components/calendar/types';
import { formatDate } from '../utils/dateFormat';
import { sessionClaims } from '../utils/session';
import { ReportAppointmentDetails } from '../components/reports/ReportAppointmentDetails';
import {
  PaymentReportDetailsFields,
  paymentReportDetailsFromIssue,
  reportSourceDetailsPayload,
  type PaymentReportDetailsState,
  type ReportDoctorOption,
  type ReportReadinessIssue
} from '../components/finance/PaymentReportDetailsFields';

interface DoctorPayoutSummary {
  doctorId: number;
  doctorName: string;
  doctorPayout: number;
  clinicShare: number;
  grossCollection: number;
  lineCount: number;
}

interface DoctorPayoutLine {
  appointmentId?: number | null;
  balanceDue?: number | null;
  doctorId: number;
  doctorName: string;
  paymentId: number;
  paymentDate: string;
  sourceType: string;
  sourceName: string;
  patientName: string;
  kind: string;
  grossAmount: number;
  invoiceDeduction: number;
  cardFeeDeduction: number;
  directCostDeduction: number;
  commissionableAmount: number;
  commissionPercentage: number;
  doctorShare: number;
  clinicShare: number;
  warning?: string | null;
}

interface PayoutReport {
  status: 'draft' | 'closed';
  closedAt?: string | null;
  doctors: DoctorPayoutSummary[];
  lines: DoctorPayoutLine[];
  warnings: string[];
  issues: ReportReadinessIssue[];
}

interface ReportsPageProps {
  onOpenDoctorTreatments: (doctorId: number, doctorName: string, month: string) => void;
}

const money = (value: number) => `Bs ${Number(value || 0).toFixed(2)}`;

const normalizeReport = (payload: unknown): PayoutReport => {
  if (Array.isArray(payload)) {
    return {
      status: 'draft',
      doctors: payload.map((row: any) => ({
        doctorId: Number(row?.doctorId || 0),
        doctorName: String(row?.doctorName || ''),
        doctorPayout: Number(row?.doctorProduction || 0),
        clinicShare: Number(row?.clinicProduction || 0),
        grossCollection: Number(row?.totalCollection || 0),
        lineCount: 0
      })),
      lines: [],
      issues: [],
      warnings: ['Payroll API is not active yet. Restart backend after applying the payout migration.']
    };
  }

  const report = payload as Partial<PayoutReport> | null;
  if (!report || !Array.isArray(report.doctors) || !Array.isArray(report.lines) || !Array.isArray(report.warnings)) {
    return {
      status: 'draft',
      doctors: [],
      lines: [],
      issues: [],
      warnings: ['Unexpected payroll report response.']
    };
  }
  return {
    status: report.status === 'closed' ? 'closed' : 'draft',
    closedAt: report.closedAt,
    doctors: report.doctors,
    lines: report.lines,
    issues: Array.isArray(report.issues) ? report.issues : [],
    warnings: report.warnings
  };
};

export const ReportsPage: React.FC<ReportsPageProps> = ({ onOpenDoctorTreatments }) => {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState<PayoutReport | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);
  const isAdmin = String(localStorage.getItem('userRole') || '').toLowerCase() === 'admin';
  const token = localStorage.getItem('token') || '';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the stored session changes together with the token
  const claims = useMemo(() => sessionClaims(), [token]);
  const canEditReportDetails = isAdmin || String(claims.CanEditPayments ?? claims.canEditPayments ?? '').toLowerCase() === 'true';
  const [doctors, setDoctors] = useState<ReportDoctorOption[]>([]);
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [issueEdits, setIssueEdits] = useState<PaymentReportDetailsState[]>([]);
  const [activeTab, setActiveTab] = useState<'payroll' | 'treatments'>('payroll');
  const [detailAppointmentId, setDetailAppointmentId] = useState<number | null>(null);

  const fetchReport = React.useCallback(async () => {
    setLoading(true);
    setActionError('');
    try {
      const [year, month] = selectedMonth.split('-');
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      const response = await fetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.REPORTS_DOCTORS)}?startDate=${year}-${month}-01&endDate=${year}-${month}-${lastDay}`,
        { headers: getAuthHeaders(localStorage.getItem('token') || '') }
      );
      if (!response.ok) throw new Error(await response.text() || 'Could not load payroll report.');
      const data = normalizeReport(await response.json());
      setReport(data);
      setIssueEdits(data.issues.map(paymentReportDetailsFromIssue));
      setSelectedDoctorId(previous => previous && data.doctors.some(doctor => doctor.doctorId === previous)
        ? previous : data.doctors[0]?.doctorId ?? null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not load payroll report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    if (!canEditReportDetails || !report?.issues.length) return;
    Promise.all([
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) }).then(response => response.ok ? response.json() : []),
      fetch(getApiUrl('/laboratories'), { headers: getAuthHeaders(token) }).then(response => response.ok ? response.json() : [])
    ]).then(([doctorRows, labRows]) => {
      setDoctors(Array.isArray(doctorRows) ? doctorRows : []);
      setLaboratories(Array.isArray(labRows) ? labRows : []);
    }).catch(() => { setDoctors([]); setLaboratories([]); });
  }, [canEditReportDetails, report?.issues.length, token]);

  const selectedDoctor = useMemo(
    () => report?.doctors.find(doctor => doctor.doctorId === selectedDoctorId) ?? null,
    [report, selectedDoctorId]
  );
  const selectedLines = useMemo(
    () => report?.lines.filter(line => line.doctorId === selectedDoctorId) ?? [],
    [report, selectedDoctorId]
  );

  const changeStatus = async (action: 'close' | 'reopen') => {
    const [year, month] = selectedMonth.split('-');
    setSaving(true);
    setActionError('');
    try {
      const response = await fetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.REPORTS_DOCTORS)}/payout-periods/${year}/${Number(month)}/${action}`,
        { method: 'POST', headers: getAuthHeaders(localStorage.getItem('token') || '') }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Could not update payroll period.');
      }
      await fetchReport();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not update payroll period.');
    } finally {
      setSaving(false);
    }
  };

  const saveIssue = async (index: number) => {
    const edit = issueEdits[index];
    const payload = reportSourceDetailsPayload(edit)[0];
    if (!payload) return;
    setSaving(true);
    setActionError('');
    try {
      const response = await fetch(getApiUrl(`/reports/sources/${edit.sourceType}/${edit.sourceId}`), {
        method: 'PUT', headers: getAuthHeaders(token), body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Could not save report details.');
      }
      await fetchReport();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save report details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, color: theme.text, background: theme.tokens.app.background, height: '100%', overflow: 'auto' }}>
      {isAdmin && (
        <div className="responsive-row" role="tablist" aria-label={t('reports.tabsLabel', 'Report type')} style={{ display: 'flex', gap: 8, marginBottom: 18, borderBottom: `1px solid ${theme.border}` }}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'payroll'}
            onClick={() => setActiveTab('payroll')}
            style={{ ...(activeTab === 'payroll' ? theme.button : theme.secondaryButton), padding: '9px 14px', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            {t('reports.doctorPayroll', 'Doctor payroll')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'treatments'}
            onClick={() => setActiveTab('treatments')}
            style={{ ...(activeTab === 'treatments' ? theme.button : theme.secondaryButton), padding: '9px 14px', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            {t('reports.treatments', 'Treatments')}
          </button>
        </div>
      )}

      {activeTab === 'payroll' ? <>
      <h2 style={{ marginTop: 0 }}>{t('reports.doctorPayroll', 'Doctor payroll')}</h2>
      <div className="responsive-row" style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'end', flexWrap: 'wrap' }}>
        <label>
          <span style={{ display: 'block', marginBottom: 6, color: theme.muted }}>{t('reports.period')}</span>
          <input type="month" value={selectedMonth} onChange={event => setSelectedMonth(event.target.value)} style={{ padding: '8px 12px', ...theme.input }} />
        </label>
        <button onClick={fetchReport} style={{ padding: '8px 16px', ...theme.button }} disabled={loading}>{t('common.refresh')}</button>
        {report && <span style={{ padding: '8px 12px', borderRadius: 6, background: report.status === 'closed' ? '#166534' : '#92400e', color: 'white' }}>
          {report.status === 'closed' ? t('reports.closed') : t('reports.draft')}
        </span>}
        {isAdmin && report?.status === 'draft' && <button disabled={saving || report.warnings.length > 0 || report.issues.length > 0} onClick={() => changeStatus('close')} style={{ padding: '8px 16px', ...theme.button }}>{t('reports.closePayroll')}</button>}
        {isAdmin && report?.status === 'closed' && <button disabled={saving} onClick={() => changeStatus('reopen')} style={{ padding: '8px 16px', ...theme.secondaryButton }}>{t('reports.reopenPayroll')}</button>}
      </div>

      {actionError && <div role="alert" style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: '#7f1d1d', color: '#fecaca' }}>{actionError}</div>}
      {report && report.warnings.length > 0 && <div role="alert" style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: '#78350f', color: '#fde68a' }}>
        <strong>{t('reports.reviewRequired')}</strong>
        <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>{report.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
      </div>}
      {report && report.issues.length > 0 && <section style={{ marginBottom: 18, display: 'grid', gap: 12 }}>
        <div role="alert" style={{ padding: 12, borderRadius: 6, background: '#78350f', color: '#fde68a' }}>
          <strong>{t('reports.readinessTitle', 'Report sources need doctor and commission details.')}</strong>
          <div>{t('reports.readinessCount', '{{count}} source(s) block payroll close.', { count: report.issues.length })}</div>
        </div>
        {report.issues.map((issue, index) => <div key={`${issue.sourceType}-${issue.sourceId}`} style={{ ...theme.card, padding: 14 }}>
          <div style={{ marginBottom: 10 }}><strong>{issue.patientName || '-'} — {issue.sourceName}</strong><div style={{ color: theme.muted, fontSize: 12 }}>{issue.sourceType} #{issue.sourceId} · {issue.missingFields.join(', ')}</div></div>
          {canEditReportDetails && issueEdits[index] ? <>
            <PaymentReportDetailsFields
              value={issueEdits[index]}
              onChange={next => setIssueEdits(current => current.map((item, itemIndex) => itemIndex === index ? next : item))}
              treatments={[]}
              laboratories={laboratories}
              doctors={doctors}
              required
              title={t('reports.repairSource', 'Repair report source')}
            />
            <button type="button" disabled={saving} onClick={() => saveIssue(index)} style={{ ...theme.button, padding: '8px 14px', marginTop: 10 }}>{t('common.save', 'Save')}</button>
          </> : <div style={{ color: theme.muted }}>{t('reports.readOnlyIssue', 'Payment editor or admin must repair this source.')}</div>}
        </div>)}
      </section>}

      {loading ? <div>{t('common.loading')}</div> : <>
        <div className="responsive-table-scroll" style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${theme.border}` }}>
          <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
            <thead><tr style={{ background: theme.inputBg, textAlign: 'left' }}>
              {[t('reports.doctor'), t('reports.collections'), t('reports.doctorPayout'), t('reports.clinicShare'), t('reports.lines'), ''].map(label => <th key={label} style={{ padding: 14, color: theme.muted }}>{label}</th>)}
            </tr></thead>
            <tbody>
              {report?.doctors.map(doctor => <tr
                key={doctor.doctorId}
                tabIndex={0}
                aria-label={doctor.doctorName}
                onClick={() => setSelectedDoctorId(doctor.doctorId)}
                onKeyDown={event => {
                  if (event.target !== event.currentTarget || event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelectedDoctorId(doctor.doctorId);
                }}
                style={{ borderBottom: `1px solid ${theme.border}`, background: doctor.doctorId === selectedDoctorId ? theme.surfaceAlt : 'transparent', cursor: 'pointer' }}
              >
                <td style={{ padding: 14 }}>{doctor.doctorName}</td>
                <td style={{ padding: 14 }}>{money(doctor.grossCollection)}</td>
                <td style={{ padding: 14, color: '#60a5fa', fontWeight: 700 }}>{money(doctor.doctorPayout)}</td>
                <td style={{ padding: 14, color: '#34d399' }}>{money(doctor.clinicShare)}</td>
                <td style={{ padding: 14 }}>{doctor.lineCount}</td>
                <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                  <button onClick={event => { event.stopPropagation(); onOpenDoctorTreatments(doctor.doctorId, doctor.doctorName, selectedMonth); }} style={{ padding: '5px 10px', ...theme.secondaryButton }}>{t('reports.appointmentDetails', 'Appointment details')}</button>
                </td>
              </tr>)}
              {!report?.doctors.length && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: theme.muted }}>{t('reports.noPayrollLines')}</td></tr>}
            </tbody>
          </table></ResponsiveTable>
        </div>

        {selectedDoctor && <div className="responsive-table-scroll" style={{ marginTop: 24, ...theme.card, padding: 18, overflowX: 'auto' }}>
          <h3 style={{ marginTop: 0 }}>{selectedDoctor.doctorName}: {money(selectedDoctor.doctorPayout)}</h3>
          <ResponsiveTable><table style={{ width: '100%', minWidth: 1100, borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', color: theme.muted }}>
              {[t('common.date'), t('common.patient'), t('reports.source'), t('reports.type'), t('reports.gross'), t('doctorTreatments.invoice'), t('reports.card'), t('reports.costs'), t('reports.base'), t('reports.balanceDue', 'Balance due'), '%', t('reports.doctor'), t('reports.clinic')].map(label => <th key={label} style={{ padding: 10 }}>{label}</th>)}
            </tr></thead>
            <tbody>{selectedLines.map((line, index) => <tr key={`${line.paymentId}-${index}`}
              tabIndex={line.appointmentId ? 0 : undefined}
              onClick={() => { if (line.appointmentId) setDetailAppointmentId(line.appointmentId); }}
              onKeyDown={event => {
                if (line.appointmentId && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault(); setDetailAppointmentId(line.appointmentId);
                }
              }}
              style={{ borderTop: `1px solid ${theme.border}`, cursor: line.appointmentId ? 'pointer' : undefined, background: Number(line.balanceDue || 0) > 0.005 ? 'rgba(239,68,68,0.12)' : undefined }}>
              <td style={{ padding: 10 }}>{formatDate(line.paymentDate)}</td><td style={{ padding: 10 }}>{line.patientName || '-'}</td><td style={{ padding: 10 }}>{line.sourceName}</td><td style={{ padding: 10 }}>{line.kind}</td>
              <td style={{ padding: 10 }}>{money(line.grossAmount)}</td><td style={{ padding: 10 }}>{money(line.invoiceDeduction)}</td><td style={{ padding: 10 }}>{money(line.cardFeeDeduction)}</td><td style={{ padding: 10 }}>{money(line.directCostDeduction)}</td>
              <td style={{ padding: 10 }}>{money(line.commissionableAmount)}</td><td style={{ padding: 10, color: Number(line.balanceDue || 0) > 0.005 ? (theme.tokens.isLight ? '#b91c1c' : '#fca5a5') : theme.text, fontWeight: Number(line.balanceDue || 0) > 0.005 ? 700 : undefined }}>{money(line.balanceDue || 0)}</td><td style={{ padding: 10 }}>{line.commissionPercentage.toFixed(2)}%</td><td style={{ padding: 10, color: '#60a5fa' }}>{money(line.doctorShare)}</td><td style={{ padding: 10, color: '#34d399' }}>{money(line.clinicShare)}</td>
            </tr>)}</tbody>
          </table></ResponsiveTable>
        </div>}
      </>}
      </> : <TreatmentUsageReport token={token} />}
      {detailAppointmentId !== null && <ReportAppointmentDetails appointmentId={detailAppointmentId} onClose={() => setDetailAppointmentId(null)} />}
    </div>
  );
};
